import { useEffect, useState, type CSSProperties } from 'react';
import { DatabaseZap, BookOpen, Sparkles, PenLine, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { DdqSessionQuestion, UserActionType } from '@/types/api.types';
import { ProvenanceTrace } from '@/features/review/components/ProvenanceTrace';
import { getTierColors, getTierLabel, getConfidenceColor } from '@/features/review/utils/tierColors';

export interface QnaIndexRowProps {
  question: DdqSessionQuestion;
  isActive: boolean;
  onSelect: (questionId: string) => void;
  onAction: (questionId: string, action: UserActionType, modifiedAnswer?: string) => void;
  /** Inline error to show when the last Accept/Edit/Reject failed to persist. */
  error?: string | null;
  className?: string;
  /**
   * Inline style for the row's `<li>` — used by aligned mode to absolutely
   * position the row at its answer's vertical offset. Compact mode omits it.
   */
  style?: CSSProperties;
}

const SOURCE_ICON = {
  vault_data: DatabaseZap,
  knowledge_base: BookOpen,
  llm: Sparkles,
  firm_required: PenLine,
} as const;

function SourceDot({ source }: { source: DdqSessionQuestion['answer_source'] }) {
  const Icon = source && source in SOURCE_ICON ? SOURCE_ICON[source] : PenLine;
  const isVault = source === 'vault_data';
  return (
    <Icon
      className={cn(
        'h-3.5 w-3.5 shrink-0',
        isVault ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/70',
      )}
      aria-hidden="true"
    />
  );
}

/**
 * One row in the Q&A index. Collapsed, it is a dense, scannable line: source
 * dot, question number, answer snippet, confidence %, and a status check when
 * actioned. Selected, it expands to reveal the full question, the provenance
 * trace (the moat — always visible), and the Accept / Edit / Reject actions.
 *
 * Confidence and provenance are never hidden behind hue alone: the numeric
 * percentage is always rendered, and the provenance chain is shown verbatim.
 */
export function QnaIndexRow({
  question,
  isActive,
  onSelect,
  onAction,
  error,
  className,
  style,
}: QnaIndexRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // The row stays mounted when it deselects/collapses, so reset any in-progress
  // edit when it loses selection — otherwise a stale draft (and the edit
  // textarea) would reappear the next time the row is expanded.
  useEffect(() => {
    if (!isActive && isEditing) {
      setIsEditing(false);
      setDraft('');
    }
  }, [isActive, isEditing]);

  const tier = getTierColors(question.response_tier);
  const action = question.userAction ?? 'Pending';
  const actioned = action !== 'Pending';
  const snippet = question.userResponse || question.suggested_response || '';

  const startEdit = () => {
    setDraft(question.userResponse || question.suggested_response || '');
    setIsEditing(true);
  };

  const saveEdit = () => {
    onAction(question.question_id, 'Modify', draft);
    setIsEditing(false);
  };

  return (
    <li
      id={`qna-row-${question.question_id}`}
      style={style}
      className={cn('block border-l-2', isActive ? tier.border : 'border-transparent', className)}
    >
      <button
        type="button"
        onClick={() => onSelect(question.question_id)}
        aria-expanded={isActive}
        aria-current={isActive ? 'true' : undefined}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
          isActive ? 'bg-muted' : 'hover:bg-muted/50',
        )}
      >
        <span className={cn('shrink-0 h-2 w-2 rounded-full', tier.bg)} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground">
            <span>Q{question.index + 1}</span>
            <span aria-hidden="true">·</span>
            <span>{getTierLabel(question.response_tier)}</span>
            <span aria-hidden="true">·</span>
            <span className={getConfidenceColor(question.confidence)}>
              {Math.round(question.confidence)}%
            </span>
          </div>
          <p className={cn('text-sm truncate', isActive ? 'text-foreground' : 'text-muted-foreground')}>
            {question.incoming_question}
          </p>
          {!isActive && snippet && (
            <p className="text-xs text-muted-foreground/80 truncate">{snippet}</p>
          )}
        </div>
        {actioned ? (
          <Check
            className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
            role="img"
            aria-label="Reviewed"
          />
        ) : (
          <SourceDot source={question.answer_source} />
        )}
      </button>

      {isActive && (
        <div className="space-y-3 px-3 pb-3 pt-1">
          <p className="text-sm font-medium text-foreground leading-relaxed">
            {question.incoming_question}
          </p>

          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="min-h-[120px] resize-y text-sm"
                aria-label="Edit answer"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="success" onClick={saveEdit}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {snippet ? (
                <div className="rounded-lg bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
                  {snippet}
                </div>
              ) : (
                <p className="text-sm text-tier-nomatch">No answer yet — edit to provide one.</p>
              )}

              {/* Provenance is a first-class, always-visible element (the moat). */}
              <ProvenanceTrace question={question} />

              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="success" onClick={() => onAction(question.question_id, 'Accept')}>
                  Accept
                </Button>
                <Button size="sm" variant="outline" onClick={startEdit}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onAction(question.question_id, 'Reject')}
                >
                  Reject
                </Button>
              </div>
              {error ? (
                <p className="text-xs text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
            </>
          )}
        </div>
      )}
    </li>
  );
}
